/**
 * Badge Component
 * Status badges and labels
 */

import { cn } from '@/lib/utils.js';

type BadgeVariant = 'default' | 'success' | 'warning' | 'error' | 'info';

interface BadgeProps {
  children: React.ReactNode;
  variant?: BadgeVariant;
  className?: string;
}

const variantStyles: Record<BadgeVariant, string> = {
  default: 'bg-gray-100 text-gray-800',
  success: 'bg-green-100 text-green-800',
  warning: 'bg-yellow-100 text-yellow-800',
  error: 'bg-red-100 text-red-800',
  info: 'bg-blue-100 text-blue-800',
};

export function Badge({ children, variant = 'default', className }: BadgeProps) {
  return (
    <span
      className={cn(
        'inline-flex items-center rounded-full px-2.5 py-0.5 text-xs font-medium',
        variantStyles[variant],
        className
      )}
    >
      {children}
    </span>
  );
}

/**
 * Status badge with predefined colors for common statuses
 */
interface StatusBadgeProps {
  status: string;
  className?: string;
}

const statusVariants: Record<string, BadgeVariant> = {
  // General
  active: 'success',
  inactive: 'default',
  pending: 'warning',
  suspended: 'error',

  // Invoice statuses
  draft: 'default',
  sent: 'info',
  viewed: 'info',
  paid: 'success',
  partial: 'warning',
  overdue: 'error',
  void: 'default',
  cancelled: 'default',

  // Payment statuses
  completed: 'success',
  failed: 'error',
  refunded: 'warning',

  // Stock statuses
  in_stock: 'success',
  low_stock: 'warning',
  out_of_stock: 'error',
};

export function StatusBadge({ status, className }: StatusBadgeProps) {
  const variant = statusVariants[status.toLowerCase()] || 'default';
  const displayStatus = status.replace(/_/g, ' ').replace(/\b\w/g, (l) => l.toUpperCase());

  return (
    <Badge variant={variant} className={className}>
      {displayStatus}
    </Badge>
  );
}
